import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "../icons/icons";
import { useUiStrings } from "../strings/ui-strings";
import type { PaginationProps } from "./types";

export const DEFAULT_PAGE_SIZES = [10, 25, 50, 100] as const;

/**
 * Page numbers to show: first, last, and a window around the current page,
 * with `null` where a gap is elided. Never more than 7 slots.
 */
export function pageWindow(page: number, totalPages: number): Array<number | null> {
	if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
	const out: Array<number | null> = [1];
	const lo = Math.max(2, page - 1);
	const hi = Math.min(totalPages - 1, page + 1);
	if (lo > 2) out.push(null);
	for (let p = lo; p <= hi; p++) out.push(p);
	if (hi < totalPages - 1) out.push(null);
	out.push(totalPages);
	return out;
}

/**
 * Offset-pagination footer. Works standalone (`<Pagination …/>`) or as the
 * DataTable footer. The `total` comes from the same query as the page —
 * `count(*) OVER()` — never a second request.
 *
 * Every word comes from `<UiStringsProvider>` (the summary is ONE string the
 * app builds per language — never "x–y" + "of" + "N" + noun glued together
 * here); `labels` overrides per table.
 */
export function Pagination({
	page,
	pageSize,
	total,
	onPageChange,
	onPageSizeChange,
	pageSizeOptions = DEFAULT_PAGE_SIZES,
	itemNoun,
	labels,
	className,
}: PaginationProps & { className?: string }) {
	const { pagination: strings, formatNumber } = useUiStrings();
	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	const current = Math.min(Math.max(1, page), totalPages);
	const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
	const to = Math.min(total, current * pageSize);

	let summary: ReactNode;
	if (total === 0) {
		summary = labels?.empty ?? (itemNoun ? strings.emptyWithNoun(itemNoun[1]) : strings.empty);
	} else if (labels?.summary) {
		summary = labels.summary({ from, to, total });
	} else if (itemNoun) {
		// Legacy path (deprecated `itemNoun`): English plural rule, kept only until callers pass `labels.summary`.
		summary = strings.summaryWithNoun({ from, to, total, noun: total === 1 ? itemNoun[0] : itemNoun[1] });
	} else {
		summary = strings.summary({ from, to, total });
	}
	const pageLabel = labels?.page ?? strings.page;

	const go = (p: number) => {
		const next = Math.min(Math.max(1, p), totalPages);
		if (next !== current) onPageChange(next);
	};

	// daisyUI fades a disabled button's border to transparent — on a `join` that
	// reads as the group's outer edge being cut off. Keep the hairline.
	// 34px, like the designer's pager (`.pgn__b`).
	const navBtn =
		"btn btn-ghost btn-sm btn-square size-[2.125rem] border-base-300 bg-base-100 text-gms-ink-2 disabled:border-base-300 disabled:bg-base-100 disabled:text-base-content/30";

	return (
		<nav
			aria-label={labels?.pagination ?? strings.label}
			className={cx(
				"flex flex-wrap items-center justify-between gap-x-[1rem] gap-y-[0.5rem] text-[0.8125rem] text-base-content/70",
				className,
			)}
		>
			<p className="tabular-nums" aria-live="polite">
				{summary}
			</p>

			<div className="flex flex-wrap items-center gap-[0.75rem]">
				{onPageSizeChange && (
					<label className="flex items-center gap-[0.5rem]">
						<span className="whitespace-nowrap">{labels?.rowsPerPage ?? strings.rowsPerPage}</span>
						<select
							className="select select-sm h-[2.125rem] w-auto min-w-[4.5rem] bg-base-100 text-[0.8125rem] tabular-nums"
							value={pageSize}
							onChange={(e) => onPageSizeChange(Number(e.target.value))}
						>
							{pageSizeOptions.map((n) => (
								<option key={n} value={n}>
									{formatNumber(n)}
								</option>
							))}
						</select>
					</label>
				)}

				<div className="join">
					<button type="button" className={cx("join-item", navBtn)} onClick={() => go(1)} disabled={current <= 1} aria-label={labels?.firstPage ?? strings.firstPage}>
						<ChevronsLeft className="size-[1rem]" />
					</button>
					<button type="button" className={cx("join-item", navBtn)} onClick={() => go(current - 1)} disabled={current <= 1} aria-label={labels?.previousPage ?? strings.previousPage}>
						<ChevronLeft className="size-[1rem]" />
					</button>
					{pageWindow(current, totalPages).map((p, i) =>
						p === null ? (
							<span
								// biome-ignore lint/suspicious/noArrayIndexKey: gaps have no identity
								key={`gap-${i}`}
								className="join-item btn btn-ghost btn-sm btn-square pointer-events-none size-[2.125rem] border-base-300 bg-base-100 text-base-content/40"
								aria-hidden
							>
								…
							</span>
						) : (
							<button
								type="button"
								key={p}
								onClick={() => go(p)}
								aria-current={p === current ? "page" : undefined}
								aria-label={pageLabel(p)}
								className={cx(
									"join-item btn btn-sm h-[2.125rem] min-w-[2.125rem] px-[0.5rem] text-[0.8125rem] tabular-nums",
									// The active page keeps the group's grey hairline (not btn-neutral's dark
									// border) so the outline runs unbroken through it.
									p === current
										? "pointer-events-none border-base-300 bg-primary text-primary-content shadow-none"
										: "btn-ghost border-base-300 bg-base-100 text-gms-ink-2",
								)}
							>
								{formatNumber(p)}
							</button>
						),
					)}
					<button type="button" className={cx("join-item", navBtn)} onClick={() => go(current + 1)} disabled={current >= totalPages} aria-label={labels?.nextPage ?? strings.nextPage}>
						<ChevronRight className="size-[1rem]" />
					</button>
					<button type="button" className={cx("join-item", navBtn)} onClick={() => go(totalPages)} disabled={current >= totalPages} aria-label={labels?.lastPage ?? strings.lastPage}>
						<ChevronsRight className="size-[1rem]" />
					</button>
				</div>
			</div>
		</nav>
	);
}

/**
 * The designer's pager (`.pgr` / `.pgn` in the GMS prototype): "Showing 1 to
 * 20 of 327" on the left, then the page-size select ("20 / page") and the
 * numbered pages with ‹ › on the right. DataTable `variant="list"` draws it
 * above AND below the table; it also works standalone.
 *
 * Phones keep the numbers (the window is at most 7 slots) and wrap under the
 * summary — the page never scrolls sideways.
 */
export function ListPagination({
	page,
	pageSize,
	total,
	onPageChange,
	onPageSizeChange,
	pageSizeOptions = DEFAULT_PAGE_SIZES,
	labels,
	position = "top",
	className,
}: PaginationProps & { position?: "top" | "bottom"; className?: string }) {
	const { pagination: strings, formatNumber } = useUiStrings();
	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	const current = Math.min(Math.max(1, page), totalPages);
	const from = total === 0 ? 0 : (current - 1) * pageSize + 1;
	const to = Math.min(total, current * pageSize);
	const summary: ReactNode = total === 0 ? (labels?.empty ?? strings.empty) : strings.showing({ from, to, total });
	const pageLabel = labels?.page ?? strings.page;

	const go = (p: number) => {
		const next = Math.min(Math.max(1, p), totalPages);
		if (next !== current) onPageChange(next);
	};

	// .pgn__b: 34px square-ish, 8px radius, 13px semibold ink-2; current = navy.
	const btn =
		"grid h-[2.125rem] min-w-[2.125rem] cursor-pointer place-items-center rounded-[0.5rem] px-[0.5rem] text-[0.8125rem] font-semibold text-gms-ink-2 tabular-nums outline-none transition-colors hover:enabled:bg-gms-soft-2 focus-visible:ring-2 focus-visible:ring-gms-navy/40 disabled:cursor-default disabled:opacity-30";

	return (
		<nav
			aria-label={labels?.pagination ?? strings.label}
			className={cx(
				"flex flex-wrap items-center gap-x-[0.875rem] gap-y-[0.5rem] text-[0.8125rem] text-gms-ink-3",
				className,
			)}
		>
			<p className="min-w-[8.75rem] flex-1 tabular-nums" aria-live={position === "top" ? "polite" : undefined}>
				{summary}
			</p>
			{onPageSizeChange && (
				<select
					aria-label={labels?.rowsPerPage ?? strings.rowsPerPage}
					className="h-[2.25rem] cursor-pointer rounded-[0.5rem] border border-gms-line bg-base-100 pr-[0.25rem] pl-[0.5rem] text-[0.8125rem] text-gms-ink-2 tabular-nums outline-none focus-visible:border-gms-navy"
					value={pageSize}
					onChange={(e) => onPageSizeChange(Number(e.target.value))}
				>
					{pageSizeOptions.map((n) => (
						<option key={n} value={n}>
							{strings.perPage(n)}
						</option>
					))}
				</select>
			)}
			<div className="flex items-center gap-[0.125rem]">
				<button type="button" className={btn} onClick={() => go(current - 1)} disabled={current <= 1} aria-label={labels?.previousPage ?? strings.previousPage}>
					<ChevronLeft className="size-[1rem]" />
				</button>
				{pageWindow(current, totalPages).map((p, i) =>
					p === null ? (
						// biome-ignore lint/suspicious/noArrayIndexKey: gaps have no identity
						<span key={`gap-${i}`} aria-hidden className="min-w-[1.375rem] text-center">
							…
						</span>
					) : (
						<button
							type="button"
							key={p}
							onClick={() => go(p)}
							aria-current={p === current ? "page" : undefined}
							aria-label={pageLabel(p)}
							className={cx(btn, p === current && "pointer-events-none bg-gms-navy text-white")}
						>
							{formatNumber(p)}
						</button>
					),
				)}
				<button type="button" className={btn} onClick={() => go(current + 1)} disabled={current >= totalPages} aria-label={labels?.nextPage ?? strings.nextPage}>
					<ChevronRight className="size-[1rem]" />
				</button>
			</div>
		</nav>
	);
}
